import { Buttons, has, type PlayerInput } from "../input.ts";
import { atan2, cos, sin, wrapAngle, yawToRadians } from "../math/trig.ts";
import {
  add,
  clampLength,
  copy,
  dot,
  length,
  normalize,
  rotateY,
  scale,
  sub,
  type Vec3,
  v3,
} from "../math/vec.ts";
import { idleFoot, idleHand, leanOffset, Part } from "../skeleton.ts";
import type { FighterState, MatchState } from "../state.ts";
import { DT, tuning } from "../tuning.ts";
import { coverZone } from "../zones.ts";
import { limbControlled } from "./strikes.ts";

const m = tuning.movement;

export function legFactor(f: FighterState): { speed: number; disabled: boolean } {
  let speed = 1;
  let disabled = false;
  for (const side of [0, 1]) {
    const thigh = f.hp[Part.ThighL + side] ?? 0;
    const shin = f.hp[Part.ShinL + side] ?? 0;
    if (thigh <= 0 || shin <= 0) disabled = true;
    else if (thigh < tuning.hp.thigh * tuning.hp.injuredBelow)
      speed *= tuning.injury.thighMoveSpeed;
  }
  if (disabled) speed *= tuning.injury.limpSpeed;
  return { speed, disabled };
}

export function armIntact(f: FighterState, side: 0 | 1): boolean {
  return (f.hp[Part.UpperArmL + side] ?? 0) > 0 && (f.hp[Part.ForearmL + side] ?? 0) > 0;
}

export function legIntact(f: FighterState, side: 0 | 1): boolean {
  return (f.hp[Part.ThighL + side] ?? 0) > 0 && (f.hp[Part.ShinL + side] ?? 0) > 0;
}

/** The leading leg's side index (0 left, 1 right) for the stance being held. */
export function leadLegSide(f: FighterState): 0 | 1 {
  return f.stance >= 0 ? 0 : 1;
}

/** Moves `value` toward `target` by at most `maxStep`. */
function approach(value: number, target: number, maxStep: number): number {
  const d = target - value;
  if (d <= maxStep && d >= -maxStep) return target;
  return value + (d > 0 ? maxStep : -maxStep);
}

/** Applies one tick of movement intent: look, facing, duck, lean, step and walking velocity. */
export function applyMovement(state: MatchState, f: FighterState, input: PlayerInput): void {
  f.lookYaw = input.yaw;
  f.lookPitch = input.pitch;
  const lookRad = yawToRadians(input.yaw);

  // Facing follows the look yaw at a finite turn rate.
  const delta = wrapAngle(lookRad - f.facing);
  const maxTurn = m.turnRate * DT;
  f.facing = wrapAngle(
    f.facing + (Math.abs(delta) <= maxTurn ? delta : delta > 0 ? maxTurn : -maxTurn),
  );

  // Crouch and slip: both are held, both take a few ticks, both move hurt volumes.
  f.duck = approach(f.duck, has(input, Buttons.DUCK) ? 1 : 0, 1 / m.duckTicks);
  const leanTarget = (has(input, Buttons.LEAN_R) ? 1 : 0) - (has(input, Buttons.LEAN_L) ? 1 : 0);
  f.lean = approach(f.lean, leanTarget, 1 / m.leanTicks);

  const legs = legFactor(f);
  // Mid-switch the feet are square and moving: no check, no step, no second switch.
  const switching = f.stance > -1 && f.stance < 1;
  const feetBusy = f.strikeLeg !== null || f.chargeLeg !== null || f.step !== null || switching;

  // Cover holds the zone its keys pick, on the side the fighter looks at
  // (design §6): the head, the body with F, the legs with Shift, where the
  // lead knee comes up to check.
  const covering = has(input, Buttons.GUARD);
  f.coverZone = covering ? coverZone(input.buttons) : -1;
  f.checking =
    f.coverZone === 2 && legIntact(f, leadLegSide(f)) && !feetBusy && state.phase !== "ended";

  // Stance switch (edge-triggered): the feet trade places over a few ticks.
  const st = tuning.stance;
  const pressedStance = has(input, Buttons.STANCE) && (f.prevButtons & Buttons.STANCE) === 0;
  if (
    pressedStance &&
    !f.checking &&
    !feetBusy &&
    !legs.disabled &&
    f.stamina >= st.switchStamina &&
    state.phase !== "ended"
  ) {
    f.southpaw = !f.southpaw;
    f.stamina -= st.switchStamina;
  }
  f.stance = approach(f.stance, f.southpaw ? -1 : 1, 2 / st.switchTicks);

  // Movement axes relative to the look yaw.
  let mx = input.moveX / 127;
  let mz = input.moveY / 127;
  const mag = Math.sqrt(mx * mx + mz * mz);
  if (mag > 1) {
    mx /= mag;
    mz /= mag;
  }
  const forwardSpeed = mz >= 0 ? m.walk : m.back;
  let local = v3(mx * m.strafe, 0, mz * forwardSpeed);
  const leaning = f.lean > 0.5 || f.lean < -0.5;
  const cap =
    (f.duck > 0.5 ? m.duck : m.walk) *
    legs.speed *
    (leaning ? m.leanMove : 1) *
    (f.checking ? tuning.check.moveFactor : 1);
  local = clampLength(local, cap);
  const s = sin(lookRad);
  const c = cos(lookRad);
  const walk = rotateY(local, s, c);

  // Step (edge-triggered): a real displacement, no invulnerability.
  const pressedStep = has(input, Buttons.STEP) && (f.prevButtons & Buttons.STEP) === 0;
  if (
    pressedStep &&
    f.step === null &&
    !f.checking &&
    !switching &&
    !legs.disabled &&
    !f.exhausted &&
    f.stamina >= tuning.stamina.step &&
    state.phase !== "ended"
  ) {
    const dir = length(walk) > 1e-6 ? normalize(walk) : v3(s, 0, c);
    f.step = { ticksLeft: m.stepTicks, dir };
    f.stamina -= tuning.stamina.step;
  }
  let stepVel = v3();
  if (f.step) {
    stepVel = scale(f.step.dir, m.step / (m.stepTicks * DT));
    f.step.ticksLeft -= 1;
    if (f.step.ticksLeft <= 0) f.step = null;
  }

  f.vel = add(add(walk, stepVel), f.impulse);
  f.impulse = scale(f.impulse, 0.8);
  if (length(f.impulse) < 0.01) f.impulse = v3();
}

/** Integrates position. Separate from intent so collisions see every fighter's velocity. */
export function integrate(f: FighterState): void {
  f.pos = add(f.pos, scale(f.vel, DT));
  f.pos.y = 0;
}

/**
 * Guard hands cover the zone picked, on the side looked at: at the temples
 * for the head, down over the ribs for the body (never lower: the legs are
 * covered by the lead knee checking, with the hands at mid-chest). Rest hands
 * are relaxed at the chest. Feet rest in stance, and the lead foot lifts
 * while checking. A quicker build moves its guard faster.
 */
export function poseLimbs(f: FighterState): void {
  const b = tuning.body;
  const lean = leanOffset(f.lean);
  const drop = m.duckDrop * f.duck + lean.drop;
  const maxHand = tuning.guard.armSpeed * f.build.speed * DT;
  const leftOk = armIntact(f, 0);
  const rightOk = armIntact(f, 1);
  let targetL = idleHand(-1, f.duck, f.lean, f.stance);
  let targetR = idleHand(1, f.duck, f.lean, f.stance);
  if (f.guard && (leftOk || rightOk)) {
    const rel = wrapAngle(yawToRadians(f.lookYaw) - f.facing);
    const flat = v3(sin(rel), 0, cos(rel));
    // Between the face and the belt: the temples for the head, over the ribs
    // for the body, mid-chest while checking, and as low as it goes when the
    // legs are asked for but the lead leg cannot check.
    const top = b.headY - drop - 0.05;
    const bottom = b.hipY - m.duckDrop * f.duck + 0.15;
    let y: number;
    if (f.checking) y = top - 0.3;
    else if (f.coverZone === 1) y = tuning.guard.bodyHands - drop;
    else if (f.coverZone === 2) y = bottom;
    else y = top;
    y = y > top ? top : y < bottom ? bottom : y;
    // Lower down the body is wider than the head: the guard reaches out to stay in front of it.
    const low = Math.min(1, (top - y) / 0.35);
    const forward = b.guardHand.forward + (b.torsoRadius - b.headRadius + 0.05) * low;
    const centre = add(v3(lean.sway, y, 0), scale(flat, forward));
    // Right vector perpendicular to the look on the ground plane.
    const right = v3(flat.z, 0, -flat.x);
    if (leftOk) targetL = sub(centre, scale(right, b.guardHand.x));
    if (rightOk) targetR = add(centre, scale(right, b.guardHand.x));
  }
  if (!limbControlled(f, 0))
    f.handL = moveHand(f.handL, leftOk ? targetL : hangingHand(-1, drop, lean.sway), maxHand);
  if (!limbControlled(f, 1))
    f.handR = moveHand(f.handR, rightOk ? targetR : hangingHand(1, drop, lean.sway), maxHand);
  const maxFoot = tuning.check.footSpeed * DT;
  const lead = leadLegSide(f);
  for (const side of [0, 1] as const) {
    const limb = side === 0 ? 2 : 3;
    if (limbControlled(f, limb)) continue;
    const sign = side === 0 ? -1 : 1;
    const target = f.checking && side === lead ? checkFoot(sign) : idleFoot(sign, f.stance);
    if (side === 0) f.footL = moveHand(f.footL, target, maxFoot);
    else f.footR = moveHand(f.footR, target, maxFoot);
  }
}

/** The lead foot lifted to check a kick: knee up and forward, shin across the line. */
function checkFoot(sign: -1 | 1): Vec3 {
  const c = tuning.check.foot;
  return v3(sign * c.x, c.y, c.z);
}

function hangingHand(side: -1 | 1, drop: number, sway: number): Vec3 {
  const b = tuning.body;
  return v3(
    side * (b.shoulderX + 0.05) + sway,
    b.shoulderY - drop - b.upperArmLength - b.forearmLength + 0.02,
    0.02,
  );
}

function moveHand(from: Vec3, to: Vec3, maxStep: number): Vec3 {
  const d = sub(to, from);
  const l = length(d);
  if (l <= maxStep || l < 1e-9) return copy(to);
  return add(from, scale(d, maxStep / l));
}

/** Yaw in steps that points from a to b, for bots and tests. */
export function yawToward(a: Vec3, b: Vec3): number {
  const d = sub(b, a);
  const rad = atan2(d.x, d.z);
  const turns = rad / (Math.PI * 2);
  const steps = Math.round((turns - Math.floor(turns)) * 65536);
  return steps % 65536;
}

/** True when the fighter's walking velocity points at the other fighter. */
export function walkingInto(f: FighterState, other: FighterState): boolean {
  const d = normalize(sub(other.pos, f.pos));
  return dot(normalize(f.vel), d) > 0.5 && length(f.vel) > 0.5;
}
